import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IProgram } from "../../interfaces/models/IEvent";
import { useEventLoader } from "./useEventLoader";
import { sessionHasDifferentVenue } from "../../utils/programVenue";
import { compareProgramsBySchedule } from "../../utils/groupProgramsByDay";

interface EditingSessionIdentity {
  title: string;
  startTime: number;
  endTime: number;
}

interface UseProgramDayVenueParams {
  eventId: string;
  eventStartDate?: number;
  sessionDate: string;
  startTime: string;
  venue: string;
  location: string;
  editingSession?: EditingSessionIdentity | null;
  updateSessionField: (field: "venue" | "location", value: string) => void;
}

const parseSessionTimestamp = (sessionDate: string, time: string): number | null => {
  if (!sessionDate || !time) {
    return null;
  }
  const [month, day, year] = sessionDate.split("/").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  if (!year || month == null || day == null || hours == null || minutes == null) {
    return null;
  }
  return Math.floor(Date.UTC(year, month - 1, day, hours, minutes, 0, 0) / 1000);
};

const sessionsOnDay = (
  programs: IProgram[],
  sessionDate: string,
): IProgram[] => {
  const date = new Date(sessionDate);
  const sessionTimestamp = Math.floor(date.getTime() / 1000);
  const targetDate = new Date(sessionTimestamp * 1000);
  const targetDay = Date.UTC(
    targetDate.getUTCFullYear(),
    targetDate.getUTCMonth(),
    targetDate.getUTCDate(),
  );
  return programs
    .filter((program) => {
      const programDate = new Date(program.startTime * 1000);
      const programDay = Date.UTC(
        programDate.getUTCFullYear(),
        programDate.getUTCMonth(),
        programDate.getUTCDate(),
      );
      return programDay === targetDay;
    })
    .sort(compareProgramsBySchedule);
};

/**
 * The first session of a day owns Program Venue. Later sessions leave those
 * fields grey until the user marks the session as held in a different location.
 */
export const useProgramDayVenue = ({
  eventId,
  eventStartDate,
  sessionDate,
  startTime,
  venue,
  location,
  editingSession,
  updateSessionField,
}: UseProgramDayVenueParams) => {
  const event = useEventLoader(eventId);
  const [heldInDifferentLocation, setHeldInDifferentLocation] = useState(false);
  const prefillKeyRef = useRef("");

  const isEditingThisSession = useCallback(
    (program: IProgram) => {
      if (!editingSession) {
        return false;
      }
      return (
        program.title === editingSession.title &&
        program.startTime === editingSession.startTime &&
        program.endTime === editingSession.endTime
      );
    },
    [editingSession],
  );

  const sessionsOnSelectedDate = useMemo(() => {
    if (!eventStartDate || !sessionDate || !event?.program) {
      return [];
    }
    return sessionsOnDay(event.program, sessionDate);
  }, [event?.program, sessionDate, eventStartDate]);

  const otherSessionsOnSelectedDate = useMemo(
    () => sessionsOnSelectedDate.filter((program) => !isEditingThisSession(program)),
    [sessionsOnSelectedDate, isEditingThisSession],
  );

  const startTimestamp = useMemo(
    () => parseSessionTimestamp(sessionDate, startTime),
    [sessionDate, startTime],
  );

  const definesDayVenue = useMemo(() => {
    if (editingSession) {
      const earliest = sessionsOnSelectedDate[0];
      if (!earliest) {
        return true;
      }
      return isEditingThisSession(earliest);
    }
    const earliestOther = otherSessionsOnSelectedDate[0];
    if (!earliestOther) {
      return true;
    }
    if (startTimestamp == null) {
      return false;
    }
    return startTimestamp < earliestOther.startTime;
  }, [
    editingSession,
    sessionsOnSelectedDate,
    otherSessionsOnSelectedDate,
    startTimestamp,
    isEditingThisSession,
  ]);

  const storedSession = useMemo(() => {
    if (!editingSession || !event?.program) {
      return null;
    }
    return event.program.find((program) => isEditingThisSession(program)) ?? null;
  }, [editingSession, event?.program, isEditingThisSession]);

  const dayVenueSession = otherSessionsOnSelectedDate[0] ?? null;
  const sessionHasOwnVenue =
    !definesDayVenue &&
    storedSession != null &&
    sessionHasDifferentVenue(storedSession, dayVenueSession ?? undefined);

  const venueChoiceKey = [
    editingSession?.title ?? "",
    editingSession?.startTime ?? "",
    editingSession?.endTime ?? "",
    sessionDate,
    definesDayVenue,
    sessionHasOwnVenue,
  ].join("|");
  const venueChoiceKeyRef = useRef("");

  useEffect(() => {
    if (venueChoiceKeyRef.current === venueChoiceKey) {
      return;
    }
    venueChoiceKeyRef.current = venueChoiceKey;
    setHeldInDifferentLocation(sessionHasOwnVenue);
  }, [venueChoiceKey, sessionHasOwnVenue]);

  useEffect(() => {
    if (definesDayVenue || heldInDifferentLocation || sessionHasOwnVenue) {
      return;
    }
    if (!venue && !location) {
      return;
    }
    updateSessionField("venue", "");
    updateSessionField("location", "");
  }, [
    definesDayVenue,
    heldInDifferentLocation,
    sessionHasOwnVenue,
    venue,
    location,
    updateSessionField,
  ]);

  useEffect(() => {
    if (!definesDayVenue || editingSession) {
      prefillKeyRef.current = "";
      return;
    }
    const prefillKey = `${sessionDate}|${definesDayVenue}`;
    if (prefillKeyRef.current === prefillKey) {
      return;
    }
    if (venue || location) {
      prefillKeyRef.current = prefillKey;
      return;
    }
    const nextVenue = dayVenueSession?.venueName || "";
    const nextLocation = dayVenueSession?.location || "";
    if (!nextVenue && !nextLocation) {
      return;
    }
    prefillKeyRef.current = prefillKey;
    updateSessionField("venue", nextVenue);
    updateSessionField("location", nextLocation);
  }, [
    definesDayVenue,
    editingSession,
    sessionDate,
    venue,
    location,
    dayVenueSession,
    updateSessionField,
  ]);

  const handleDifferentLocationToggle = () => {
    if (heldInDifferentLocation) {
      updateSessionField("venue", "");
      updateSessionField("location", "");
      setHeldInDifferentLocation(false);
      return;
    }
    setHeldInDifferentLocation(true);
  };

  return {
    definesDayVenue,
    heldInDifferentLocation,
    handleDifferentLocationToggle,
    venueFieldsDisabled: !definesDayVenue && !heldInDifferentLocation,
  };
};
